import { clearLocalKioskDataForNewAccount } from '../../shared/localDb';
import { supabase } from '../../shared/supabase/supabaseClient';
import { primeAccountSetupFromBootstrap } from '../../shell/session/accountSetupService';
import { hydrateSessionCachesFromBootstrap } from '../../shell/session/hydrateSessionCachesFromBootstrap';
import { primeSessionOrganizationId } from '../../shell/session/sessionOrganization';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { useBusinessProfileStore } from '../../shell/session/store/useBusinessProfileStore';
import { fetchUserBranches } from '../../auth/application/authBranches';
import { setOnboardingCompleteOffline } from '../../shell/session/offlineAccountCache';
import { saveBranchCapabilitiesJson, saveOrganizationBusinessProfileOffline } from '../../shell/connectivity/businessProfileCache';
import type { BusinessTypeCode, OrganizationBusinessProfile } from '../../shell/session/domain/businessProfile';
import { parseBusinessProfilePayload } from '../../shell/session/domain/businessProfile';
import { parseOperationMode, type OperationModeMap } from '../../shell/session/domain/operationMode';
import { applyOnboardingCatalogPack, fetchBusinessTypes } from './businessTypeService';
import { syncCatalogSnapshotFromRemote } from '../../catalog/infra-api';
import { logBusinessAuditEvent } from '../../shell/audit/auditLogs';

async function reloadBranches(): Promise<void> {
  const sessionStore = useSessionStore.getState();
  sessionStore.setBranchesLoading(true);
  try {
    const branches = await fetchUserBranches({ force: true });
    sessionStore.setBranches(branches);
  } finally {
    sessionStore.setBranchesLoading(false);
  }
}

async function hydrateUserAfterOnboarding(
  userId: string,
  organizationId: string,
  businessProfile: OrganizationBusinessProfile | null,
  branchIds: string[],
  branchId?: string,
  fullHydration = true,
): Promise<void> {
  primeSessionOrganizationId(userId, organizationId);
  if (fullHydration) {
    primeAccountSetupFromBootstrap(userId, {
      requiresOwnerOnboarding: false,
      ownerSetupCompletedAt: new Date().toISOString(),
      organizationId,
      branchCount: branchIds.length,
      organizationClosedAt: null,
      awaitingEmployeeInvitation: false,
    });
  }
  if (businessProfile) {
    useBusinessProfileStore.getState().setProfile(businessProfile);
    try {
      await saveOrganizationBusinessProfileOffline(businessProfile);
    } catch (e) {
      console.warn('[Onboarding] No se pudo cachear business profile', e);
    }
    if (branchId && businessProfile.capabilities?.length) {
      try {
        await saveBranchCapabilitiesJson(branchId, businessProfile.capabilities);
      } catch (e) {
        console.warn('[Onboarding] No se pudo cachear capabilities de sucursal', e);
      }
    }
  }
  if (fullHydration) {
    try {
      const payload = await hydrateSessionCachesFromBootstrap(userId, { force: true });
      if (payload?.branches) {
        useSessionStore.getState().setBranches(payload.branches);
      }
    } catch (e) {
      console.warn('[Onboarding] Re-bootstrap parcial falló', e);
    }
  }
  try {
    await setOnboardingCompleteOffline(userId);
  } catch (e) {
    console.warn('[Onboarding] No se pudo persistir flag offline', e);
  }
}

async function resolveOnboardingPackCode(
  businessTypeCode: BusinessTypeCode,
  businessProfile: OrganizationBusinessProfile | null,
  countryCode: string,
  explicitPackCode?: string | null,
): Promise<string | null> {
  if (typeof explicitPackCode === 'string' && explicitPackCode.trim()) {
    return explicitPackCode.trim();
  }

  const fromConfig = businessProfile?.business_config?.onboarding_pack_code;
  if (typeof fromConfig === 'string' && fromConfig.trim()) {
    return fromConfig.trim();
  }

  try {
    const types = await fetchBusinessTypes(countryCode);
    const match = types.find((t) => t.code === businessTypeCode);
    if (match?.onboarding_pack_code) return match.onboarding_pack_code;
  } catch (e) {
    console.warn('[Onboarding] No se pudo resolver pack desde list_business_types', e);
  }

  if (businessTypeCode === 'neighborhood_store') return 'neighborhood_store_ar_v1';
  if (businessTypeCode === 'retail_counter') return 'retail_counter_ar_v1';
  if (businessTypeCode === 'services_beauty') return 'services_beauty_ar_v1';
  if (businessTypeCode === 'services_workshop') return 'services_workshop_ar_v1';
  if (businessTypeCode === 'quick_service_food') return 'qsf_cafe_ar_v1';
  return null;
}

export type EnsureOrganizationResult = {
  organization_id: string;
  created: boolean;
  branch_ids: string[];
  /** Sucursal recién creada (`create_branch_for_my_organization`). */
  branch_id?: string;
};

export type EnsureOrganizationOptions = {
  businessTypeCode?: BusinessTypeCode;
  countryCode?: string;
  applyCatalogPack?: boolean;
  /** QSF identity flavor (pizzeria|burger|parrilla|cafe). */
  verticalFlavor?: string | null;
  /** Pack override; when set, preferred over business_types.onboarding_pack_code. */
  onboardingPackCode?: string | null;
  /** Patch de `operation_mode` tras crear (o reintentar) el alta. */
  operationModePatch?: OperationModeMap;
  /** Texto libre cuando el comercio elige Otros. */
  requestedVerticalLabel?: string | null;
  /** Nombre visible del dueño (customer-facing en turnos). Opcional. */
  ownerName?: string | null;
};

async function applyOperationModePatch(
  patch: OperationModeMap,
  businessProfile: OrganizationBusinessProfile | null,
): Promise<OrganizationBusinessProfile | null> {
  const { data, error } = await supabase.rpc('update_my_organization_operation_mode', {
    p_patch: patch,
  });
  if (error) throw error;
  if (!businessProfile) return businessProfile;
  const nextMode =
    data && typeof data === 'object' && !Array.isArray(data)
      ? parseOperationMode({ operation_mode: data as Record<string, unknown> })
      : parseOperationMode({ operation_mode: patch });
  return {
    ...businessProfile,
    business_config: { ...businessProfile.business_config, operation_mode: nextMode },
  };
}

/**
 * Crea organización + primera sucursal en Supabase (RPC idempotente) y recarga sucursales en sesión.
 */
export async function ensureMyOrganization(
  organizationName: string,
  branchName: string,
  options?: EnsureOrganizationOptions,
): Promise<EnsureOrganizationResult> {
  const { data, error } = await supabase.rpc('ensure_my_organization', {
    p_organization_name: organizationName.trim() || null,
    p_branch_name: branchName.trim() || null,
    p_business_type_code: options?.businessTypeCode ?? 'retail_counter',
    p_country_code: options?.countryCode ?? 'AR',
    p_vertical_flavor: options?.verticalFlavor ?? null,
    p_onboarding_pack_code: options?.onboardingPackCode ?? null,
    p_requested_vertical_label: options?.requestedVerticalLabel ?? null,
    p_owner_name: options?.ownerName?.trim() || null,
  });

  if (error) {
    throw error;
  }

  const raw = data as Record<string, unknown> | null;
  if (!raw || typeof raw.organization_id !== 'string') {
    throw new Error('Respuesta inválida del servidor');
  }

  const branchIds = Array.isArray(raw.branch_ids)
    ? (raw.branch_ids as unknown[]).map((x) => String(x))
    : [];

  const created = Boolean(raw.created);
  let businessProfile = parseBusinessProfilePayload(raw.business_profile ?? null);
  const selectedBusinessType = options?.businessTypeCode ?? 'retail_counter';

  if (created) {
    try {
      await clearLocalKioskDataForNewAccount();
    } catch (e) {
      console.warn('[Onboarding] No se pudo limpiar datos locales para cuenta nueva', e);
    }
  }

  if (created && options?.applyCatalogPack && businessProfile) {
    const countryCode = options.countryCode ?? businessProfile.country_code ?? 'AR';
    const packCode = await resolveOnboardingPackCode(
      selectedBusinessType,
      businessProfile,
      countryCode,
      options.onboardingPackCode,
    );
    if (packCode) {
      try {
        await applyOnboardingCatalogPack(packCode);
        try {
          await syncCatalogSnapshotFromRemote({ force: true, forceFull: true });
        } catch (syncErr) {
          console.warn('[Onboarding] No se pudo sincronizar snapshot de catálogo tras pack', syncErr);
        }
      } catch (e) {
        console.warn('[Onboarding] No se pudo aplicar pack de catálogo', e);
      }
    }
  }

  if (options?.operationModePatch && Object.keys(options.operationModePatch).length > 0) {
    businessProfile = await applyOperationModePatch(options.operationModePatch, businessProfile);
  }

  await reloadBranches();

  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (user?.id) {
    await hydrateUserAfterOnboarding(user.id, raw.organization_id, businessProfile, branchIds);
  }

  if (created) {
    void logBusinessAuditEvent('analytics.business_type_selected', {
      businessTypeCode: selectedBusinessType,
      organization_id: raw.organization_id,
      country_code: options?.countryCode ?? businessProfile?.country_code ?? 'AR',
    });
  }

  return {
    organization_id: raw.organization_id,
    created,
    branch_ids: branchIds,
  };
}

/**
 * Alta de un negocio adicional (multi-org dueño). La RPC deja la org nueva activa.
 */
export async function createAdditionalOrganization(
  organizationName: string,
  branchName: string,
  options?: EnsureOrganizationOptions,
): Promise<EnsureOrganizationResult> {
  const { data, error } = await supabase.rpc('create_additional_organization', {
    p_organization_name: organizationName.trim() || null,
    p_branch_name: branchName.trim() || null,
    p_business_type_code: options?.businessTypeCode ?? 'retail_counter',
    p_country_code: options?.countryCode ?? 'AR',
    p_vertical_flavor: options?.verticalFlavor ?? null,
    p_onboarding_pack_code: options?.onboardingPackCode ?? null,
    p_requested_vertical_label: options?.requestedVerticalLabel ?? null,
    p_owner_name: options?.ownerName?.trim() || null,
  });

  if (error) {
    throw error;
  }

  const raw = data as Record<string, unknown> | null;
  if (!raw || typeof raw.organization_id !== 'string') {
    throw new Error('Respuesta inválida del servidor');
  }

  const branchIds = Array.isArray(raw.branch_ids)
    ? (raw.branch_ids as unknown[]).map((x) => String(x))
    : [];

  let businessProfile = parseBusinessProfilePayload(raw.business_profile ?? null);
  const selectedBusinessType = options?.businessTypeCode ?? 'retail_counter';

  if (options?.applyCatalogPack && businessProfile) {
    const countryCode = options.countryCode ?? businessProfile.country_code ?? 'AR';
    const packCode = await resolveOnboardingPackCode(
      selectedBusinessType,
      businessProfile,
      countryCode,
      options.onboardingPackCode,
    );
    if (packCode) {
      try {
        await applyOnboardingCatalogPack(packCode);
        try {
          await syncCatalogSnapshotFromRemote({ force: true, forceFull: true });
        } catch (syncErr) {
          console.warn('[Onboarding] No se pudo sincronizar catálogo tras org adicional', syncErr);
        }
      } catch (e) {
        console.warn('[Onboarding] No se pudo aplicar pack en org adicional', e);
      }
    }
  }

  if (options?.operationModePatch && Object.keys(options.operationModePatch).length > 0) {
    businessProfile = await applyOperationModePatch(options.operationModePatch, businessProfile);
  }

  await reloadBranches();

  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (user?.id) {
    await hydrateUserAfterOnboarding(user.id, raw.organization_id, businessProfile, branchIds);
  }

  void logBusinessAuditEvent('analytics.business_type_selected', {
    businessTypeCode: selectedBusinessType,
    organization_id: raw.organization_id,
    country_code: options?.countryCode ?? businessProfile?.country_code ?? 'AR',
    additional_organization: true,
  });

  return {
    organization_id: raw.organization_id,
    created: true,
    branch_ids: branchIds,
  };
}

export async function createBranchForMyOrganization(branchName: string): Promise<EnsureOrganizationResult> {
  const { data, error } = await supabase.rpc('create_branch_for_my_organization', {
    p_branch_name: branchName.trim() || null,
  });

  if (error) {
    throw error;
  }

  const raw = data as Record<string, unknown> | null;
  if (!raw || typeof raw.organization_id !== 'string') {
    throw new Error('Respuesta inválida del servidor');
  }

  const branchIds = Array.isArray(raw.branch_ids)
    ? (raw.branch_ids as unknown[]).map((x) => String(x))
    : [];
  const branchId = typeof raw.branch_id === 'string' ? raw.branch_id : undefined;
  const businessProfile = parseBusinessProfilePayload(raw.business_profile ?? null);

  await reloadBranches();

  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (user?.id) {
    await hydrateUserAfterOnboarding(user.id, raw.organization_id, businessProfile, branchIds, branchId, false);
  }

  return {
    organization_id: raw.organization_id,
    created: false,
    branch_ids: branchIds,
    branch_id: branchId,
  };
}
